// ADR-0052 §3 / V101-2-5, D-CI-4: the `bulk` capture shape for AI usage.
// Raw provider pages land in R2 (bulkPageKey); only daily aggregates reach
// OLTP (ai_usage_rollups). The writer copies an ALLOWLIST of fields, so a
// prompt/response body cannot be stored even if a collector passes one.
//
// ADR-0102 §1 (migration 810, V113-2-1): a row has a `rollup_kind`. `usage` is today's provider usage
// bucket; `invocation_metrics`, `filter_metrics` and `drift_metrics` are provider monitoring metrics
// (CloudWatch, Azure Monitor, Cloud Monitoring, AI Gateway analytics) whose extra numbers live in
// `metrics_json` under a per-kind key allowlist. A row that names a resource is attributed through the
// configuration-item register: exactly one linked system → `system_id`; otherwise the CI alone.
import { normalizeExternalId } from './configuration-item-types.js';
export const ROLLUP_KINDS = ['usage', 'invocation_metrics', 'filter_metrics', 'drift_metrics'];
/** Columns every kind may fill. */
export const ROLLUP_COLUMNS = ['requests', 'input_tokens', 'output_tokens', 'cost_minor'];
/** The `metrics_json` allowlist per kind (D-CI-4: numbers only, never a body). */
export const ROLLUP_METRICS = Object.freeze({
    usage: [],
    invocation_metrics: ['errors', 'throttles', 'latency_ms_avg', 'cache_hits', 'tokens_total'],
    filter_metrics: ['blocked', 'harmful'],
    drift_metrics: ['drift_deviation'],
});
/** Every field an anomaly rule may measure (ADR-0102 §2). */
export const ROLLUP_FIELDS = [
    'requests', 'input_tokens', 'output_tokens', 'cost_minor',
    'errors', 'throttles', 'latency_ms_avg', 'cache_hits', 'tokens_total', 'blocked', 'harmful', 'drift_deviation',
];
/** Fields combined by maximum (not sum) across a day's rows. */
export const MAX_COMBINED_FIELDS = ['latency_ms_avg', 'drift_deviation'];
/** Same refusal as the customer collector (D-CI-4): a key that names a body is never stored. */
export const ROLLUP_BODY_KEYS = /^(prompt|prompts|completion|completions|response|responses|payload|payloads|vector|vectors|embedding|embeddings|messages|content|text|document|documents|points|objects)$/i;
export class UsageRollupValidationError extends Error {
    constructor(message) { super(message); this.name = 'UsageRollupValidationError'; }
}
const DAY = /^\d{4}-\d{2}-\d{2}$/;
// Review M5: strict character classes (a customer push is free input). Provider keys are catalog-style
// slugs; channels are snake_case; model ids are vendor ids (no spaces, quotes or markup).
const PROVIDER_KEY_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const CHANNEL_RE = /^[a-z][a-z0-9_]{0,39}$/;
const MODEL_RE = /^[A-Za-z0-9._:/@+-]{1,200}$/;
const CURRENCY_RE = /^[A-Za-z]{3}$/;
const matching = (v, re, field) => {
    if (!re.test(v))
        throw new UsageRollupValidationError(`${field} is malformed`);
    return v;
};
const RESOURCE_ID = /^[A-Za-z0-9/][A-Za-z0-9._:/@#+=-]{0,299}$/;
const DIMENSION = /^[A-Za-z0-9][A-Za-z0-9 ._:/@#+=-]{0,119}$/;
const REF_KIND = /^[a-z][a-z0-9_]{1,40}$/;
const nonNegInt = (v, field) => {
    if (v === undefined || v === null)
        return 0;
    if (typeof v !== 'number' || !Number.isFinite(v) || v < 0)
        throw new UsageRollupValidationError(`${field} must be a non-negative number`);
    return Math.round(v);
};
const short = (v, field, max, allowEmpty = false) => {
    if (typeof v !== 'string' || (!allowEmpty && !v.trim()) || v.length > max)
        throw new UsageRollupValidationError(`${field} is required (≤ ${max} chars)`);
    return v.trim();
};
/** Copy only the allowlisted aggregate fields (D-CI-4: no content can pass through). */
export function sanitizeUsageRow(raw) {
    const day = short(raw.day, 'day', 10);
    if (!DAY.test(day) || Number.isNaN(Date.parse(`${day}T00:00:00Z`)))
        throw new UsageRollupValidationError('day must be YYYY-MM-DD');
    const kind = (raw.rollup_kind ?? 'usage');
    if (!ROLLUP_KINDS.includes(kind))
        throw new UsageRollupValidationError(`rollup_kind must be one of ${ROLLUP_KINDS.join(', ')}`);
    const allowed = ROLLUP_METRICS[kind];
    const metrics = {};
    if (raw.metrics !== undefined && raw.metrics !== null) {
        if (typeof raw.metrics !== 'object' || Array.isArray(raw.metrics))
            throw new UsageRollupValidationError('metrics must be an object of numbers');
        for (const [k, v] of Object.entries(raw.metrics)) {
            if (ROLLUP_BODY_KEYS.test(k))
                throw new UsageRollupValidationError(`metrics key ${k} names a body field (D-CI-4)`);
            if (!allowed.includes(k))
                throw new UsageRollupValidationError(`metrics key ${k} is not allowed for ${kind}`);
            if (typeof v !== 'number' || !Number.isFinite(v) || v < 0)
                throw new UsageRollupValidationError(`metrics.${k} must be a non-negative number`);
            metrics[k] = Math.round(v * 1e6) / 1e6;
        }
    }
    let ref = null;
    if (raw.resource_ref) {
        const r = raw.resource_ref;
        ref = { provider_key: short(r.provider_key, 'resource_ref.provider_key', 64), kind: short(r.kind, 'resource_ref.kind', 40), external_id: short(r.external_id, 'resource_ref.external_id', 300) };
        if (!REF_KIND.test(ref.kind) || !RESOURCE_ID.test(ref.external_id))
            throw new UsageRollupValidationError('resource_ref is malformed');
    }
    const dimension = raw.dimension === undefined || raw.dimension === '' ? '' : short(raw.dimension, 'dimension', 120);
    if (dimension && !DIMENSION.test(dimension))
        throw new UsageRollupValidationError('dimension is malformed');
    const resource_key = ref ? `${ref.kind}:${ref.external_id}` : '';
    return {
        provider_key: matching(short(raw.provider_key, 'provider_key', 64), PROVIDER_KEY_RE, 'provider_key'),
        access_channel: matching(short(raw.access_channel, 'access_channel', 40), CHANNEL_RE, 'access_channel'),
        // Metric rows need not be split by model (a guardrail, a gateway).
        model: kind === 'usage' ? matching(short(raw.model, 'model', 200), MODEL_RE, 'model') : ((m) => (m === '' ? m : matching(m, MODEL_RE, 'model')))(short(raw.model ?? '', 'model', 200, true)),
        // ADR-0102 §1: a non-usage row's key_hash is derived (callers cannot set it), so it never collides with a usage row.
        key_hash: kind === 'usage'
            ? (raw.key_hash === undefined ? '' : short(raw.key_hash, 'key_hash', 128, true))
            : `${kind}|${resource_key}|${dimension}`,
        day,
        requests: nonNegInt(raw.requests, 'requests'),
        input_tokens: nonNegInt(raw.input_tokens, 'input_tokens'),
        output_tokens: nonNegInt(raw.output_tokens, 'output_tokens'),
        cost_minor: raw.cost_minor === undefined || raw.cost_minor === null ? null : nonNegInt(raw.cost_minor, 'cost_minor'),
        currency: raw.currency ? matching(short(raw.currency, 'currency', 3), CURRENCY_RE, 'currency').toUpperCase() : null,
        rollup_kind: kind,
        metrics,
        resource_ref: ref,
        resource_key,
        dimension,
    };
}
/** The anomaly subject of a row (ADR-0102 §4): the system, else the CI, else (metric rows) the resource, else (usage rows) the provider; null = not evaluated. */
export function rollupSubject(row) {
    if (row.system_id)
        return `system:${row.system_id}`;
    if (row.component_id)
        return `ci:${row.component_id}`;
    // Review H1: an unresolved METRIC row is never summed into the provider's usage series (the same
    // traffic would count twice); it is its own resource subject, or not evaluated at all.
    if ((row.rollup_kind ?? 'usage') !== 'usage')
        return row.resource_key ? `resource:${row.resource_key}` : null;
    return `provider:${row.provider_key}`;
}
/**
 * ADR-0102 §1: resolve a metric's resource to its CI (merges followed) and the CI to its one linked
 * system. Several linked systems → the CI alone (a shared resource's volume is never split by guess).
 */
export async function attributeRollupResource(db, workspaceId, ref, ctx = {}) {
    const external = normalizeExternalId(ref.provider_key.toLowerCase(), ref.kind, ref.external_id, ctx);
    if (!external)
        return { component_id: null, system_id: null };
    const found = (await db.query(`SELECT component_id, merged_into_id, retired_at FROM ai_components WHERE workspace_id = ? AND external_id = ?`, [workspaceId, external])).results;
    const ids = new Set();
    for (const row of found) {
        let cur = row;
        for (let hops = 0; cur && cur.merged_into_id && hops < 5; hops++) {
            cur = (await db.query(`SELECT component_id, merged_into_id, retired_at FROM ai_components WHERE workspace_id = ? AND component_id = ?`, [workspaceId, cur.merged_into_id])).results[0];
        }
        if (cur && !cur.merged_into_id && !cur.retired_at)
            ids.add(cur.component_id);
    }
    if (ids.size !== 1)
        return { component_id: null, system_id: null };
    const component_id = [...ids][0];
    const systems = (await db.query(`SELECT DISTINCT system_id FROM assets WHERE workspace_id = ? AND component_id = ? AND retired_at IS NULL AND system_id IS NOT NULL ORDER BY system_id`, [workspaceId, component_id])).results;
    return { component_id, system_id: systems.length === 1 ? systems[0].system_id : null };
}
/**
 * Upsert daily buckets for one connector, REPLACING a day's values (the
 * provider reports daily totals), so a re-run or retried page never
 * double-counts. Scoped by workspace_id. Returns the anomaly subjects and days
 * it touched (ADR-0102 §3: evaluation runs after each rollup write).
 */
export async function upsertUsageRollups(db, input) {
    const nowIso = input.now.toISOString();
    let written = 0;
    const subjects = new Map();
    const cache = new Map();
    for (const raw of input.rows) {
        const r = sanitizeUsageRow(raw);
        let attr = { component_id: null, system_id: null };
        if (r.resource_ref) {
            const k = `${r.resource_ref.provider_key}|${r.resource_ref.kind}|${r.resource_ref.external_id}`;
            attr = cache.get(k) ?? await attributeRollupResource(db, input.workspace_id, r.resource_ref, input.component_ctx);
            cache.set(k, attr);
        }
        await db.execute(`INSERT INTO ai_usage_rollups (rollup_id, workspace_id, connector_id, provider_key, access_channel, model, key_hash, day,
                                     requests, input_tokens, output_tokens, cost_minor, currency, updated_at,
                                     rollup_kind, system_id, component_id, resource_key, dimension, metrics_json)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT (workspace_id, connector_id, provider_key, access_channel, model, key_hash, day) DO UPDATE SET
         requests = excluded.requests, input_tokens = excluded.input_tokens, output_tokens = excluded.output_tokens,
         cost_minor = excluded.cost_minor, currency = excluded.currency, updated_at = excluded.updated_at,
         rollup_kind = excluded.rollup_kind, system_id = excluded.system_id, component_id = excluded.component_id,
         resource_key = excluded.resource_key, dimension = excluded.dimension, metrics_json = excluded.metrics_json`, [input.newId(), input.workspace_id, input.connector_id, r.provider_key, r.access_channel, r.model, r.key_hash, r.day,
            r.requests, r.input_tokens, r.output_tokens, r.cost_minor, r.currency, nowIso,
            r.rollup_kind, attr.system_id, attr.component_id, r.resource_key, r.dimension, JSON.stringify(r.metrics)]);
        written++;
        const subject = rollupSubject({ ...attr, provider_key: r.provider_key, rollup_kind: r.rollup_kind, resource_key: r.resource_key });
        if (!subject)
            continue;
        if (!subjects.has(subject))
            subjects.set(subject, new Set());
        subjects.get(subject).add(r.day);
    }
    return { written, subjects: [...subjects].map(([subject, days]) => ({ subject, days: [...days].sort() })) };
}
/** R2 key for a raw bulk page (ADR-0052 §3); expire `bulk/` after 30 days with an R2 lifecycle rule. */
export function bulkPageKey(workspaceId, connectorId, runId, page) {
    const safe = (s) => s.replace(/[^A-Za-z0-9_.-]/g, '_');
    return `bulk/${safe(workspaceId)}/${safe(connectorId)}/${safe(runId)}/${String(page).padStart(5, '0')}.json`;
}
/** Land one raw provider page in R2 (never in OLTP). */
export async function writeBulkPage(storage, key, page) {
    await storage.put(key, JSON.stringify(page));
}
//# sourceMappingURL=usage-rollup-service.js.map